/**
 * Decisions: what the engine is waiting for, and the answers players send (docs/design/03 §4, ADR-0002).
 *
 * Every tier uses decisions for setup (mulligan CR 117, battlefield choice CR 488.5). Tier 3 drives the
 * whole game through them; tier 2's "resolve automatically" asks its choices through the same kinds.
 * The table layer declares every kind as plain data so one `GameState` serves all tiers; the shapes of
 * the tier-2/3 kinds are owned by the rules and authority layers, which may refine them (with a
 * `STATE_SCHEMA` bump and a migration).
 */
import type { JsonObject } from '../../kernel/json';
import type { BattlefieldId, ChainItemId, DecisionId, ObjectId, PlayerId } from './ids';
import type { CardRef } from './objects';
import type { Domain } from './resources';
import type { ZoneRef } from './zones';

export const DECISION_KINDS = [
  'mulligan',
  'chooseBattlefield',
  'priority',
  'chooseTargets',
  'chooseObjects',
  'chooseMode',
  'payCost',
  'yesNo',
  'chooseNumber',
  'order',
  'assignDamage',
] as const;
export type DecisionKind = (typeof DECISION_KINDS)[number];

/**
 * Structured, localisable prompt text: a message key plus parameters; card references are object ids
 * the viewer can see. The client renders the sentence ("Choose up to 2 cards to recycle").
 */
export interface Prompt {
  key: string;
  params: Record<string, string | number | boolean | null>;
}

/** Fields every decision has. */
export interface DecisionBase {
  /** Unique; commands must quote it. */
  id: DecisionId;
  /** State `seq` when it was issued. */
  seq: number;
  player: PlayerId;
  prompt: Prompt;
  /** The card or ability asking (highlighted in the UI), or `null`. */
  source: ObjectId | null;
  /** While a discretionary play can still be rewound (CR 358.5), the player may answer `{ kind: 'cancel' }`. */
  cancellable: boolean;
  /** Whether the per-decision timer runs (protocol timing constants). */
  timer: 'decision' | 'none';
}

/** Choose up to `max` cards from the opening hand to set aside (CR 117.1). Asked in turn order. */
export interface MulliganDecision extends DecisionBase {
  kind: 'mulligan';
  /** The hand: the cards that may be set aside. */
  options: ObjectId[];
  max: number;
}

/** One battlefield option: a card at setup (CR 488.5), or a location as a destination (tier 3). */
export interface BattlefieldOption {
  id: string;
  card: CardRef | null;
  battlefield: BattlefieldId | null;
}

/** Choose a battlefield: setup selection (CR 488.5, hidden until everyone chose) or a destination (tier 3). */
export interface ChooseBattlefieldDecision extends DecisionBase {
  kind: 'chooseBattlefield';
  purpose: 'setup' | 'destination';
  options: BattlefieldOption[];
}

/** Hints attached to a priority action so the client can highlight without a round trip (03 §4). */
export interface ActionHints {
  destinations: ZoneRef[];
  firstTargets: ObjectId[];
  cost: CostSpec | null;
}

/** Everything legal right now in a priority window (tier 3; 03 §4, §11). `pass` is always present. */
export type LegalAction =
  | { id: string; type: 'play'; object: ObjectId; from: ZoneRef; hints: ActionHints }
  | {
      id: string;
      type: 'activate';
      object: ObjectId;
      ability: number;
      hints: ActionHints;
    }
  | { id: string; type: 'standardMove'; object: ObjectId; destinations: ZoneRef[] }
  | { id: string; type: 'hide'; object: ObjectId; destinations: BattlefieldId[] }
  | { id: string; type: 'pass'; label: 'pass' | 'endTurn' | 'passFocus' };

/** Play / activate / move / hide / pass (main phase, showdown focus, chain priority; tier 3). */
export interface PriorityDecision extends DecisionBase {
  kind: 'priority';
  window: 'main' | 'showdownFocus' | 'chain';
  actions: LegalAction[];
}

/** Targets for a pending chain item (CR 355); the client dims everything else. */
export interface ChooseTargetsDecision extends DecisionBase {
  kind: 'chooseTargets';
  item: ChainItemId | null;
  /** Index of the instruction the targets are for (needed for ignored vs negated, 03 §7.4). */
  instruction: number | null;
  options: ObjectId[];
  min: number;
  max: number;
}

/** Non-targeted choices on resolution: "choose a unit", look at the top N and pick, discard N. */
export interface ChooseObjectsDecision extends DecisionBase {
  kind: 'chooseObjects';
  options: ObjectId[];
  min: number;
  max: number;
  /** The zone the options come from, when they are in a hidden zone the player is allowed to see. */
  zone: ZoneRef | null;
}

/** "Choose one / two" (modal spells and abilities). */
export interface ChooseModeDecision extends DecisionBase {
  kind: 'chooseMode';
  modes: { id: string; prompt: Prompt }[];
  min: number;
  max: number;
}

/** A total cost to pay (CR 356): Energy plus Power symbols plus non-standard costs. */
export interface CostSpec {
  energy: number;
  /** Power symbols: a domain, or `any` for [A] (CR 135.2.e.5). */
  power: { domain: Domain | 'any'; n: number }[];
  /** Non-standard costs (kill a friendly unit, discard, spend XP…), shaped by the rules layer. */
  other: JsonObject[];
}

/** A payment source the player can toggle: a rune (exhaust for Energy, recycle for Power), a pool unit, an [Add] ability. */
export interface PaymentSource {
  id: string;
  object: ObjectId | null;
  uses: ('exhaust' | 'recycle' | 'spend' | 'activate')[];
}

/** One use of one source in a payment plan. */
export interface PaymentChoice {
  source: string;
  use: 'exhaust' | 'recycle' | 'spend' | 'activate';
}

/** Energy/Power payment with rune toggles (03 §8). Confirm stays disabled until the plan is valid. */
export interface PayCostDecision extends DecisionBase {
  kind: 'payCost';
  cost: CostSpec;
  sources: PaymentSource[];
  /** The planner's default plan (pre-selected toggles), or `null` when none exists. */
  suggested: PaymentChoice[] | null;
}

/** "You may", optional additional costs, Repeat. */
export interface YesNoDecision extends DecisionBase {
  kind: 'yesNo';
}

/** X costs, split damage amounts. */
export interface ChooseNumberDecision extends DecisionBase {
  kind: 'chooseNumber';
  min: number;
  max: number;
}

/** Simultaneous triggers, replacement order, recycle order to the Rune Deck (CR 417.5.a). */
export interface OrderDecision extends DecisionBase {
  kind: 'order';
  /** Ids of the things to order (chain items, objects, effects), as the rules layer defines them. */
  items: string[];
}

/**
 * One unit receiving combat damage, with the constraints the engine checks the assignment against (CR 467.2.c):
 * computed by the rules layer from everything that applies (marked damage, prevention, replacement effects,
 * "any damage is lethal", ignored [Tank]; CR 467.2.c.4.a, 467.2.c.5), so the client never has to rebuild them.
 */
export interface DamageRecipient {
  object: ObjectId;
  /**
   * Damage that makes it lethal (at least 1; CR 467.2.c.4.a). `Number.MAX_SAFE_INTEGER` when all of it would be
   * prevented: it can never get lethal, so it takes the excess last.
   */
  lethal: number;
  /** Assignment order (CR 467.2.c.6): 0 [Tank] (first), 1 normal, 2 [Backline] (last). */
  group: 0 | 1 | 2;
}

/**
 * Damage assignment: combat damage within the lethal-first, Tank and Backline constraints (CR 467.2.c), or a
 * spell's damage split among its targets (CR 355.14.f: at least 1 each).
 */
export interface AssignDamageDecision extends DecisionBase {
  kind: 'assignDamage';
  amount: number;
  targets: ObjectId[];
  /**
   * Combat damage: each target's constraints, in `targets` order (schema 4). `null` for a split (no lethal order)
   * and for combat decisions issued before schema 4.
   */
  recipients: DamageRecipient[] | null;
}

export type Decision =
  | MulliganDecision
  | ChooseBattlefieldDecision
  | PriorityDecision
  | ChooseTargetsDecision
  | ChooseObjectsDecision
  | ChooseModeDecision
  | PayCostDecision
  | YesNoDecision
  | ChooseNumberDecision
  | OrderDecision
  | AssignDamageDecision;

/** The answer to a decision, by kind. `cancel` rewinds a cancellable decision (CR 358.5). */
export type Answer =
  | { kind: 'mulligan'; setAside: ObjectId[] }
  | { kind: 'chooseBattlefield'; option: string }
  | { kind: 'priority'; action: string }
  | { kind: 'chooseTargets'; targets: ObjectId[] }
  | { kind: 'chooseObjects'; objects: ObjectId[] }
  | { kind: 'chooseMode'; modes: string[] }
  | { kind: 'payCost'; payments: PaymentChoice[] }
  | { kind: 'yesNo'; yes: boolean }
  | { kind: 'chooseNumber'; n: number }
  | { kind: 'order'; order: string[] }
  | { kind: 'assignDamage'; assignment: { target: ObjectId; amount: number }[] }
  | { kind: 'cancel' };

export type AnswerKind = Answer['kind'];
